"use client";

import {
	type CSSProperties,
	cloneElement,
	isValidElement,
	type ReactElement,
	type ReactNode,
	type Ref,
} from "react";
import { useReveal } from "@/lib/hooks/useReveal";

export type RevealAnimation =
	| "fade-left"
	| "fade-right"
	| "fade-top"
	| "fade-bottom"
	| "draw-left"
	| "draw-right"
	| "draw-top"
	| "draw-bottom";

interface RevealProps {
	children: ReactNode;
	/** Generic preset. Omit when the child has custom CSS keyed on `data-visible`. */
	animation?: RevealAnimation;
	/** Re-trigger when the element leaves and re-enters. Default true. */
	repeat?: boolean;
	/** IntersectionObserver threshold. Default 0.15. */
	threshold?: number;
	/** CSS transition-delay, ms. Used for staggers between sibling reveals. */
	delay?: number;
	/**
	 * When true, merge data-attrs/ref onto the single child element via
	 * `cloneElement` instead of wrapping it in a div. Note: can fail during
	 * Fast Refresh if the child resolves to undefined mid-HMR.
	 */
	asChild?: boolean;
}

type ChildWithRevealProps = ReactElement<{
	ref?: Ref<HTMLElement>;
	style?: CSSProperties;
	"data-visible"?: string;
	"data-animate"?: string;
}>;

export const Reveal = ({
	children,
	animation,
	repeat,
	threshold,
	delay,
	asChild = false,
}: RevealProps) => {
	const { ref, visible } = useReveal<HTMLElement>({ threshold, repeat });

	if (asChild) {
		if (!isValidElement(children)) return null;
		const child = children as ChildWithRevealProps;
		const childStyle = child.props.style;
		const mergedStyle: CSSProperties | undefined =
			delay !== undefined ? { ...childStyle, transitionDelay: `${delay}ms` } : childStyle;
		return cloneElement(child, {
			ref,
			"data-visible": visible ? "true" : "false",
			...(animation && { "data-animate": animation }),
			...(mergedStyle && { style: mergedStyle }),
		});
	}

	return (
		<div
			ref={ref as Ref<HTMLDivElement>}
			data-visible={visible ? "true" : "false"}
			{...(animation && { "data-animate": animation })}
			style={delay !== undefined ? { transitionDelay: `${delay}ms` } : undefined}
		>
			{children}
		</div>
	);
};
